import React, { useState, useEffect } from 'react';
import { storageGet, storageSet } from '@/lib/nativeStorage';
import { API_URL } from '@/config/api';
import { t } from '@/i18n';

/**
 * Email Verification Banner — reminds an unverified user ONCE per browser.
 *
 * Owner decision 2026-09-27: the reminder shows until the person closes it or
 * asks for the email again; either is remembered in this browser, so the same
 * banner does not come back on every page. A newly verified address hides it
 * for good through the status check.
 */
const DISMISSED_KEY = 'buzzbit_email_verification_dismissed';

export const EmailVerificationBanner: React.FC = () => {
  const [show, setShow] = useState(false);
  const [resending, setResending] = useState(false);
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const checkVerification = async () => {
      const token = storageGet('buzzbit_token');
      if (!token || storageGet(DISMISSED_KEY)) return;
      try {
        const res = await fetch(`${API_URL}/api/auth/verification-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          // GET /auth/verification-status answers `{ verified }`; the banner
          // read `emailVerified` and therefore never showed to anyone.
          const verified = data.data?.verified ?? data.verified ?? data.data?.emailVerified ?? data.emailVerified;
          setShow(verified === false);
        }
      } catch { /* silent */ }
    };
    void checkVerification();
  }, []);

  const dismiss = (): void => {
    storageSet(DISMISSED_KEY, new Date().toISOString());
    setShow(false);
  };

  const handleResend = async () => {
    setResending(true);
    setFailed(false);
    try {
      const token = storageGet('buzzbit_token');
      const res = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token || ''}`, 'Content-Type': 'application/json' },
      });
      // "Sent!" only when the server actually sent it; a refusal keeps the
      // button so the person can try again.
      if (res.ok) {
        setSent(true);
        storageSet(DISMISSED_KEY, new Date().toISOString());
      } else {
        setFailed(true);
      }
    } catch {
      setFailed(true);
    } finally { setResending(false); }
  };

  if (!show) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-200 text-amber-800 px-4 py-2 flex items-center justify-between gap-3 text-sm">
      <span>{t('emailVerification.banner')}</span>
      <div className="flex items-center gap-2">
        {failed && <span className="text-red-700 text-xs">{t('emailVerification.resendFailed')}</span>}
        {!sent ? (
          <button
            onClick={handleResend}
            disabled={resending}
            className="bg-amber-600 text-white px-3 py-1 rounded font-medium hover:bg-amber-700 transition-colors text-xs"
          >
            {resending ? t('emailVerification.sending') : t('emailVerification.resend')}
          </button>
        ) : (
          <span className="text-emerald-600 font-medium text-xs">{t('emailVerification.sent')}</span>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('emailVerification.dismiss')}
          className="text-amber-700 hover:text-amber-900 px-1 text-base leading-none"
        >
          ×
        </button>
      </div>
    </div>
  );
};
